import _ from 'underscore';

import notifications from 'notifications';
import {Thunk} from 'redux-stores/Store';
import {schemaSpecFetched, setSchemaSpecError} from 'schemas/spec/SchemaConfigReduxActions';
import getSpecError from 'ui-library/spec-editor/getSpecError';
import {withLoadingScreen} from 'util/Promises';

import {RequestTemplate, RequestTemplateSpec} from './RequestTemplateConstants';
import * as RequestTemplateDAO from './RequestTemplateDAO';

export enum RequestTemplateActionType {
  CREATE_REQUEST_TEMPLATE_COMPLETED = 'CREATE_REQUEST_TEMPLATE_COMPLETED ',
  FETCH_ALL_REQUEST_TEMPLATES_COMPLETED = 'FETCH_ALL_REQUEST_TEMPLATES_COMPLETED',
  UPDATE_REQUEST_TEMPLATE_COMPLETED = 'UPDATE_REQUEST_TEMPLATE_COMPLETED',
  REQUEST_TEMPLATES_UPDATED_FROM_GQL = 'REQUEST_TEMPLATES_UPDATED_FROM_GQL',
}

export interface CreateRequestTemplateCompleted {
  type: RequestTemplateActionType.CREATE_REQUEST_TEMPLATE_COMPLETED;
  template: RequestTemplate;
}

export function createRequestTemplateCompleted(template: RequestTemplate): CreateRequestTemplateCompleted {
  return {
    type: RequestTemplateActionType.CREATE_REQUEST_TEMPLATE_COMPLETED,
    template,
  };
}

export interface FetchAllRequestTemplatesCompleted {
  type: RequestTemplateActionType.FETCH_ALL_REQUEST_TEMPLATES_COMPLETED;
  templates: Array<RequestTemplate>;
  requestTemplatesById: {[templateId: string]: RequestTemplate};
}

export function fetchAllRequestTemplatesCompleted(
  templates: Array<RequestTemplate>,
  requestTemplatesById: {[templateId: string]: RequestTemplate}
): FetchAllRequestTemplatesCompleted {
  return {
    type: RequestTemplateActionType.FETCH_ALL_REQUEST_TEMPLATES_COMPLETED,
    templates,
    requestTemplatesById,
  };
}

export interface RequestTemplatesUpdatedFromGQL {
  type: RequestTemplateActionType.REQUEST_TEMPLATES_UPDATED_FROM_GQL;
  templates: Array<RequestTemplate>;
}

export function requestTemplatesUpdatedFromGQL(
  templates: Array<RequestTemplate>
): RequestTemplatesUpdatedFromGQL {
  return {
    type: RequestTemplateActionType.REQUEST_TEMPLATES_UPDATED_FROM_GQL,
    templates,
  };
}

export interface UpdateRequestTemplateCompleted {
  type: RequestTemplateActionType.UPDATE_REQUEST_TEMPLATE_COMPLETED;
  template: RequestTemplate;
}

export function updateRequestTemplateCompleted(template: RequestTemplate): UpdateRequestTemplateCompleted {
  return {
    type: RequestTemplateActionType.UPDATE_REQUEST_TEMPLATE_COMPLETED,
    template,
  };
}

export function fetchAllRequestTemplates(): Thunk<Promise<void>> {
  return async (dispatch) => {
    const response = await RequestTemplateDAO.fetchAllRequestTemplates();
    const requestTemplatesById = _.indexBy(response.templates, 'id');
    dispatch(fetchAllRequestTemplatesCompleted(response.templates, requestTemplatesById));
  };
}

export function fetchRequestTemplateSpec(requestTemplateId: string): Thunk<void> {
  return async (dispatch) => {
    const {schemaSpecs} = await RequestTemplateDAO.bulkFetchRequestTemplates([requestTemplateId]);
    const [schemaSpec] = schemaSpecs;
    dispatch(schemaSpecFetched(requestTemplateId, schemaSpec));
  };
}

export function updateRequestTemplate(requestTemplateId: string, spec: RequestTemplateSpec): Thunk<void> {
  return async (dispatch) => {
    try {
      const {schemaSpec, template} = await withLoadingScreen(
        RequestTemplateDAO.updateRequestTemplate(requestTemplateId, spec),
        'Updating...'
      );
      dispatch(schemaSpecFetched(requestTemplateId, schemaSpec));
      dispatch(updateRequestTemplateCompleted(template));
      notifications.success('Updated request template successfully');
    } catch (error) {
      const specError = getSpecError(error);
      if (specError) {
        dispatch(setSchemaSpecError(specError));
      } else {
        throw error;
      }
    }
  };
}

export type RequestTemplateAction =
  | CreateRequestTemplateCompleted
  | FetchAllRequestTemplatesCompleted
  | RequestTemplatesUpdatedFromGQL
  | UpdateRequestTemplateCompleted;
